import { DonnaApiResponse } from '@omers-packages/next-donna-gateway-handler';
import { DashboardError } from 'components/v2/molecular/dashboardManager/DashboardManager';
import { first, isEmpty } from 'lodash';
import { getData } from 'services/services';
import { getApiErrorMessageByCode } from 'utils';
import { isDonnaSuccess, toLoggerError } from 'utils/api';
import clientSide from 'utils/logger/client-side';
import {
  DisabilityDashboardContent,
  DisabilityDashboardResponse
} from './types';

export interface DisabilityDashboardSuccess {
  success: true;
  isEmpty?: boolean;
  data: DisabilityDashboardContent[];
}

export const refreshDisabilityDashboardCache = async (
  employerGroupNo: string
) => {
  try {
    const response = await getData(
      `/proxy/api/v1/dashboard/disability/search/cache-refresh/${employerGroupNo}`
    );

    clientSide.ok('Disability dashboard cache successfully refreshed', {
      ddAttr: 'disabilityDashboard:cacheRefresh',
      employerGroup: employerGroupNo
    });

    if (!isEmpty(response.errors)) {
      clientSide.error(
        'There was an error refreshing the Disability Dashboard Cache',
        {
          ...toLoggerError(first(response.errors)),
          ddAttr: 'disabilityDashboard:cacheRefresh:expectedError'
        }
      );
    }
  } catch (error) {
    clientSide.error(
      'There was an unexpected error refreshing the Disability Dashboard Cache',
      {
        ...toLoggerError(error),
        ddAttr: 'disabilityDashboard:cacheRefresh:unexpectedError'
      },
      error as Error
    );
  }
};

export const getDisabilityDashboard = async (
  employerGroupNo: string
): Promise<DisabilityDashboardSuccess | DashboardError> => {
  try {
    const response: DonnaApiResponse<DisabilityDashboardResponse> = await getData(
      `/proxy/api/v1/dashboard/disability/search?employerGroupNo=${employerGroupNo}`
    );
    if (!isDonnaSuccess(response)) {
      clientSide.error(
        `Error fetching disability dashboard results for employer number: ${employerGroupNo}`,
        response.result.errors
      );
      return {
        success: false,
        errors: response.result.errors.map(error => ({
          code: error.code ?? 'GENERIC_ERROR',
          message: error.message ?? '',
          uiErrorMessage: getApiErrorMessageByCode(
            error.code ?? 'GENERIC_ERROR'
          ),
          path: error.path ?? ''
        }))
      };
    }
    clientSide.ok('Fetched disability dashboard results');
    return {
      success: true,
      isEmpty: response.result.data.empty,
      data: response.result.data.content
    };
  } catch (error) {
    clientSide.error(
      `Unexpected error fetching disability dashboard results for employer number: ${employerGroupNo}`,
      toLoggerError(error)
    );
    return {
      success: false,
      errors: [
        {
          code: 'GENERIC_ERROR',
          message: '',
          uiErrorMessage: getApiErrorMessageByCode('GENERIC_ERROR'),
          path: `/proxy/api/v1/dashboard/disability/search?employerGroupNo=${employerGroupNo}`
        }
      ]
    };
  }
};
